import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import { channelMemberGuard } from "./access-filters";

export const notificationKeywordsQueries = {
  byUser: defineQuery(({ ctx: { sub } }) =>
    zql.notification_keywords.where("user_id", sub),
  ),
  byUserAndWorkspace: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.notification_keywords
        .where("user_id", sub)
        .where("workspace_id", workspaceId),
  ),
};

export const deviceTokensQueries = {
  byUser: defineQuery(({ ctx: { sub } }) =>
    zql.device_tokens.where("user_id", sub),
  ),
};

export const webPushSubscriptionsQueries = {
  byUser: defineQuery(({ ctx: { sub } }) =>
    zql.web_push_subscriptions.where("user_id", sub),
  ),
};

export const notificationEventsQueries = {
  byRecipient: defineQuery(({ ctx: { sub } }) =>
    zql.notification_events
      .where("recipient_user_id", sub)
      .orderBy("created_at", "desc"),
  ),
  unreadByRecipient: defineQuery(({ ctx: { sub } }) =>
    zql.notification_events
      .where("recipient_user_id", sub)
      .where("read_at", "IS", null)
      .orderBy("created_at", "desc"),
  ),
  unreadByRecipientWithRelated: defineQuery(({ ctx: { sub } }) =>
    zql.notification_events
      .where("recipient_user_id", sub)
      .where("read_at", "IS", null)
      .related("actor")
      .related("channel")
      .related("message")
      .orderBy("created_at", "desc")
      .limit(200),
  ),
  unreadByRecipientInWorkspaceWithRelated: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.notification_events
        .where("recipient_user_id", sub)
        .where("workspace_id", workspaceId)
        .where("read_at", "IS", null)
        .related("actor")
        .related("channel")
        .related("message")
        .orderBy("created_at", "desc")
        .limit(200),
  ),
  byMessageAndRecipient: defineQuery(
    z.string(),
    ({ args: messageId, ctx: { sub } }) =>
      zql.notification_events
        .where("message_id", messageId)
        .where("recipient_user_id", sub),
  ),
};

export const notificationPreferencesQueries = {
  byUser: defineQuery(({ ctx: { sub } }) =>
    zql.notification_preferences.where("user_id", sub),
  ),
  byUserAndWorkspace: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.notification_preferences
        .where("user_id", sub)
        .where("workspace_id", workspaceId)
        .one(),
  ),
};

export const channelNotificationOverridesQueries = {
  byUser: defineQuery(({ ctx: { sub } }) =>
    zql.channel_notification_overrides.where("user_id", sub),
  ),
  byUserAndChannel: defineQuery(
    z.string(),
    ({ args: channelId, ctx: { sub } }) =>
      zql.channel_notification_overrides
        .where("user_id", sub)
        .where("channel_id", channelId)
        .one(),
  ),
};

export const dndSettingsQueries = {
  byUser: defineQuery(({ ctx: { sub } }) =>
    zql.dnd_settings.where("user_id", sub).one(),
  ),
};

// Channel-membership gate ONLY — mirrors `messages.*` and `pins.*`. The
// previous `is_private=false AND FULL_MEMBER_ROLES` widening branch leaked
// thread-follower metadata (which threads exist, who follows them) for PUBLIC
// channels to full members who had never joined. Following a thread requires
// being an active member of its channel.
export const threadFollowersQueries = {
  byThread: defineQuery(z.string(), ({ args: threadId, ctx: { sub } }) =>
    zql.thread_followers
      .where("thread_id", threadId)
      .whereExists("thread", (tq) =>
        tq.whereExists("channel", channelMemberGuard(sub)),
      ),
  ),
  byUser: defineQuery(({ ctx: { sub } }) =>
    zql.thread_followers
      .where("user_id", sub)
      .whereExists("thread", (tq) =>
        tq.whereExists("channel", channelMemberGuard(sub)),
      ),
  ),
  byThreadAndUser: defineQuery(z.string(), ({ args: threadId, ctx: { sub } }) =>
    zql.thread_followers
      .where("thread_id", threadId)
      .where("user_id", sub)
      .whereExists("thread", (tq) =>
        tq.whereExists("channel", channelMemberGuard(sub)),
      )
      .one(),
  ),
};

export const pinsQueries = {
  // Channel-membership gate ONLY — mirrors `messages.*`. The previous
  // `is_private=false AND FULL_MEMBER_ROLES` widening branch leaked pin
  // metadata (which message_ids are pinned, by whom, when) for PUBLIC
  // channels to full members who had never joined. Public-channel identity
  // is discoverable via `channels.publicByWorkspaceForBrowse`, but the
  // contents (pins, followers) require actually being a channel member.
  byChannel: defineQuery(z.string(), ({ args: channelId, ctx: { sub } }) =>
    zql.pins
      .where("channel_id", channelId)
      .whereExists("channel", channelMemberGuard(sub)),
  ),
  byMessage: defineQuery(z.string(), ({ args: messageId, ctx: { sub } }) =>
    zql.pins
      .where("message_id", messageId)
      .whereExists("channel", channelMemberGuard(sub))
      .one(),
  ),
};
